import {
  ASSETS_STORE, DB_NAME, DB_VERSION, FLIGHTS_STORE,
  type OfflineAssetRecord, type OfflineFlightRecord,
} from "./schema";

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function openOfflineDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(ASSETS_STORE)) {
        const store = db.createObjectStore(ASSETS_STORE, { keyPath: "key" });
        store.createIndex("flightId", "flightId", { unique: false });
      }
      if (!db.objectStoreNames.contains(FLIGHTS_STORE)) {
        db.createObjectStore(FLIGHTS_STORE, { keyPath: "flightId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(
  storeName: string, mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => Promise<T> | T,
): Promise<T> {
  const db = await openOfflineDb();
  try {
    const tx = db.transaction(storeName, mode);
    const result = await fn(tx.objectStore(storeName));
    await new Promise<void>((res, rej) => {
      tx.oncomplete = () => res();
      tx.onerror = () => rej(tx.error);
      tx.onabort = () => rej(tx.error);
    });
    return result;
  } finally {
    db.close();
  }
}

export async function putAsset(record: OfflineAssetRecord): Promise<void> {
  await withStore(ASSETS_STORE, "readwrite", async (s) => {
    await promisify(s.put(record));
  });
}

export async function getAsset(key: string): Promise<OfflineAssetRecord | null> {
  return withStore(ASSETS_STORE, "readonly", async (s) => {
    const out = await promisify(s.get(key));
    return (out ?? null) as OfflineAssetRecord | null;
  });
}

export async function deleteAsset(key: string): Promise<void> {
  await withStore(ASSETS_STORE, "readwrite", async (s) => {
    await promisify(s.delete(key));
  });
}

export async function listAssetsByFlight(flightId: string): Promise<OfflineAssetRecord[]> {
  return withStore(ASSETS_STORE, "readonly", async (s) => {
    const idx = s.index("flightId");
    const out = await promisify(idx.getAll(flightId));
    return (out ?? []) as OfflineAssetRecord[];
  });
}

export async function putFlight(record: OfflineFlightRecord): Promise<void> {
  await withStore(FLIGHTS_STORE, "readwrite", async (s) => {
    await promisify(s.put(record));
  });
}

export async function getFlight(flightId: string): Promise<OfflineFlightRecord | null> {
  return withStore(FLIGHTS_STORE, "readonly", async (s) => {
    const out = await promisify(s.get(flightId));
    return (out ?? null) as OfflineFlightRecord | null;
  });
}

export async function deleteFlight(flightId: string): Promise<void> {
  // Caller is responsible for deleting the asset records first via
  // listAssetsByFlight + deleteAsset. This function only removes the metadata.
  await withStore(FLIGHTS_STORE, "readwrite", async (s) => {
    await promisify(s.delete(flightId));
  });
}

export async function listFlights(): Promise<OfflineFlightRecord[]> {
  return withStore(FLIGHTS_STORE, "readonly", async (s) => {
    const out = await promisify(s.getAll());
    return (out ?? []) as OfflineFlightRecord[];
  });
}
